import { type Label, LabelType } from '@piccolohealth/pbs-common';
import type { OnChangeRequest } from '@piccolohealth/ui';
import React from 'react';
import { useCreateLabelMutation } from '../../../graphql/hooks/useCreateLabelMutation';
import { useCreateParticipantRestrictivePracticeLabelMutation } from '../../../graphql/hooks/useCreateParticipantRestrictivePracticeLabelMutation';
import { useDeleteParticipantRestrictivePracticeLabelMutation } from '../../../graphql/hooks/useDeleteParticipantRestrictivePracticeLabelMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { usePermission } from '../../../hooks/usePermission';
import { LabelsControl } from '../../labels/LabelsControl';

export interface ManageLabelsRestrictivePracticeControlProps {
  participantId: string;
  restrictivePracticeId: string;
  value: string[];
  isDisabled?: boolean;
}

export const ManageParticipantRestrictivePracticeLabelsControl = (
  props: ManageLabelsRestrictivePracticeControlProps,
) => {
  const { restrictivePracticeId, participantId, isDisabled, value } = props;
  const { organization, errorToast } = useAppContext();

  const createLabelMutation = useCreateLabelMutation({ retry: false });
  const createParticipantRestrictivePracticeLabelMutation =
    useCreateParticipantRestrictivePracticeLabelMutation({ retry: false });
  const deleteParticipantRestrictivePracticeLabelMutation =
    useDeleteParticipantRestrictivePracticeLabelMutation({ retry: false });

  const labelManagePermission = usePermission('manage', 'label');
  const participantRestrictivePracticeLabelManagePermission = usePermission(
    'manage',
    'participantRestrictivePracticeLabel',
  );

  const onChange = React.useCallback(
    (request: OnChangeRequest<Label>) => {
      switch (request.action) {
        case 'CREATE': {
          return createLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              request: {
                type: LabelType.ParticipantRestrictivePractice,
                color: 'blue',
                name: request.value.value,
              },
            })
            .catch((err: Error) => errorToast(`Error creating label ${err.message}`));
        }
        case 'ADD': {
          return createParticipantRestrictivePracticeLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              participantId,
              restrictivePracticeId,
              labelId: request.value.value,
            })
            .catch((err: Error) =>
              errorToast(`Error adding label to participant restrictive practice: ${err.message}`),
            );
        }

        case 'REMOVE': {
          return deleteParticipantRestrictivePracticeLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              participantId,
              restrictivePracticeId,
              labelId: request.value.value,
            })
            .catch((err: Error) =>
              errorToast(
                `Error removing label from participant restrictive practice: ${err.message}`,
              ),
            );
        }
      }
    },
    [
      createLabelMutation,
      organization.id,
      errorToast,
      createParticipantRestrictivePracticeLabelMutation,
      participantId,
      restrictivePracticeId,
      deleteParticipantRestrictivePracticeLabelMutation,
    ],
  );

  return (
    <LabelsControl
      onChange={onChange}
      value={value}
      isDisabled={isDisabled || !participantRestrictivePracticeLabelManagePermission}
      canManage={labelManagePermission}
      variant='selectGhost'
      type={LabelType.ParticipantRestrictivePractice}
    />
  );
};
